/*  MUI  */
import { DeleteOutline, ExpandMore } from '@mui/icons-material';
import {
    Button,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Tab,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';
import { ManagedTable } from 'application/Shared/Components/Table';
import {
    NoteTypes,
    NoteTypesReversed,
    NoteTypesValues
} from 'application/Shared/Constants';
import { Helpers, Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import { Application } from 'objects/Application';
import { Tenant, TenantName } from 'objects/Tenant';

/*  React  */
import React, { Component } from 'react';
import { Importer, ImporterField } from 'react-csv-importer';
import 'react-csv-importer/dist/index.css';
import { withTranslation } from 'react-i18next';

/*  Constants  */
const endpoint = '/communityNote';

const headers = [
    {
        renderHeader: i18n.t('Common.timestamp'),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'dateCreated',
        width: '14%'
    },
    {
        renderHeader: i18n.t('Common.application'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        width: '20%'
    },
    {
        renderHeader: i18n.t('Common.type'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'type',
        width: '11%'
    },
    {
        renderHeader: (
            <Typography style={{ fontWeight: 'bold' }}>
                {i18n.t('Common.note/recommendation')}
            </Typography>
        ),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'message',
        width: '20%'
    },
    {
        renderHeader: i18n.t('Common.published_by'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'tenant.name',
        width: '18%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        sortable: false,
        width: '20%'
    }
];

const renderTenant = (row) => {
    const { tenant } = row;

    return (
        <Tenant data={tenant}>
            <div style={{ width: '100%' }}>
                <TenantName />
            </div>
        </Tenant>
    );
};

const parameters = {
    includeApp: true,
    includeTenant: true
};

const searchFields = ['id', 'type', 'message'];

/*  State  */
const state = () => ({
    csvError: false,
    modifying: false,
    modifyMessage: '',
    modifyType: '',
    rerender: false,
    tab: 'notes'
});

class Dashboard extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidUpdate() {
        const { reload } = this.props;
        if (reload) {
            this.reload();
        }
    }

    componentWillUnmount() {
        this.unload();
    }

    reload = () => {
        this.setState(state());
    };

    unload = () => {
        this.setState(state());
    };

    rerender = (callback) => {
        this.setState({ rerender: true }, () =>
            this.setState({ rerender: false }, () => {
                if (callback) {
                    callback();
                }
            })
        );
    };

    save = async (id) => {
        const { modifyType, modifyMessage } = this.state;
        debug.i('Modifying Community App Note', id);
        try {
            await axios.put(`${endpoint}/${id}`, {
                message: modifyMessage,
                type: NoteTypesReversed[modifyType]
            });
            Notify.s('Note has been approved');
            this.rerender(
                this.setState({
                    modifying: false,
                    modifyMessage: '',
                    modifyType: ''
                })
            );
        } catch (e) {
            debug.e('Unable to Modify', e);
            Notify.e('Unable to Modify Note', id);
        }
    };

    approve = async (id) => {
        debug.i('Approving Community App Note', id);
        try {
            await axios.put(`${endpoint}/${id}`, {
                approved: true
            });
            Notify.s('Note has been approved');
            this.rerender();
        } catch (e) {
            const err = 'Unable to Approve Note';
            debug.e(err, e);
            Notify.e(err, id);
        }
    };

    delete = async (id) => {
        debug.i('Deleting Community App Note', id);
        try {
            await axios.delete(`${endpoint}/${id}`);
            Notify.s('Note has been deleted');
            this.rerender();
        } catch (e) {
            const err = 'Unable to Delete Note';
            debug.e(err, e);
            Notify.e(err, id);
        }
    };

    import = async (params) => {
        const { rows } = params;
        if (Helpers.isEmpty(rows)) {
            return true;
        }

        const payload = [];

        rows.forEach((note) => {
            let { applicationId, type, message } = note;
            applicationId = applicationId.trim();
            type = type.trim();
            message = message.trim();
            payload.push({ applicationId, approved: true, message, type });
        });
        debug.dev('Importing Community Notes', payload, endpoint);

        try {
            await axios.post(`${endpoint}/bulk`, payload);
        } catch (e) {
            Notify.e('Unable to Import Notes');
            debug.e('Unable to Import Community Notes', e);
            this.setState({ csvError: true });
            return false;
        }

        return true;
    };

    renderActions = (row) => {
        const { t } = this.props;
        const { modifying } = this.state;
        const { id, type, message, approved } = row;

        if (modifying === id) {
            return (
                <>
                    <Button
                        size="small"
                        onClick={() => this.save(id)}
                        color="primary"
                        variant="contained"
                        style={{ marginRight: '4px' }}
                    >
                        {t('Common.save')}
                    </Button>
                    <Button
                        size="small"
                        onClick={() => this.setState({ modifying: false })}
                        variant="outlined"
                        color="primary"
                    >
                        {t('Common.cancel')}
                    </Button>
                </>
            );
        }

        return (
            <>
                <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    sx={{ marginRight: '4px' }}
                    onClick={() => this.approve(id)}
                    disabled={approved}
                >
                    {t('Common.approve')}
                </Button>
                <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ marginRight: '4px' }}
                    onClick={() =>
                        this.setState({
                            modifying: id,
                            modifyMessage: message,
                            modifyType: NoteTypes[type]
                        })
                    }
                >
                    {t('Common.edit')}
                </Button>
                <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => this.delete(id)}
                >
                    <DeleteOutline />
                </IconButton>
            </>
        );
    };

    renderTypeCell = (row) => {
        const { t } = this.props;
        const { modifying, modifyType } = this.state;
        const { id, type } = row;

        if (modifying !== id) {
            return t(`Notes.${NoteTypes[type]}`);
        }

        return (
            <Select
                IconComponent={ExpandMore}
                variant="standard"
                value={modifyType}
                onChange={(e) => this.setState({ modifyType: e.target.value })}
                style={{ width: '100%' }}
            >
                {NoteTypesValues.map((item) => (
                    <MenuItem key={`${id}_${item}`} value={item}>
                        {t(`Notes.${item}`)}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    renderNoteCell = (row) => {
        const { modifying, modifyMessage } = this.state;
        const { id, message } = row;
        if (modifying !== id) {
            return message;
        }

        return (
            <TextField
                fullWidth
                value={modifyMessage}
                multiline
                onChange={(e) =>
                    this.setState({ modifyMessage: e.target.value })
                }
            />
        );
    };

    renderRow = (row) => {
        const { dateCreated, application, applicationId } = row;

        return (
            <>
                <TableCell>
                    <DateFormat date={dateCreated} small />
                </TableCell>
                <TableCell>
                    {application ? (
                        <Application
                            data={application}
                            nameLinkTo="/globaladmin/application"
                        >
                            <ApplicationNameLinkCell withCategory />
                        </Application>
                    ) : (
                        <>
                            Unknown Application
                            <br />
                            <small>{applicationId}</small>
                        </>
                    )}
                </TableCell>
                <TableCell>{this.renderTypeCell(row)}</TableCell>
                <TableCell>{this.renderNoteCell(row)}</TableCell>
                <TableCell>{renderTenant(row)}</TableCell>
                <TableCell align="right">{this.renderActions(row)}</TableCell>
            </>
        );
    };

    renderCSVUpload = () => {
        const { csvError } = this.state;

        return (
            <Importer
                assumeNoHeaders={false}
                restartable={false}
                onComplete={() => {
                    if (!csvError) {
                        Notify.s('Successfully imported community notes.');
                    }
                }}
                processChunk={async (rows) => {
                    this.import({ rows });
                }}
                onClose={() => {
                    this.reload();
                }}
            >
                <ImporterField name="applicationId" label="Application ID" />
                <ImporterField name="type" label="Type" />
                <ImporterField name="message" label="Note" />
            </Importer>
        );
    };

    renderTable = () => {
        const { rerender } = this.state;

        const toggles = [
            {
                defaultEnabled: true,
                key: 'approved',
                parameter: { field: 'approved', value: false },
                toggleText: 'Waiting for Approval'
            }
        ];

        return (
            <ManagedTable
                headers={headers}
                parameters={parameters}
                renderBody={this.renderRow}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rowHeight={42}
                rowsApi={endpoint}
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="date"
                sortKey="dateCreated"
                toggles={toggles}
            />
        );
    };

    renderTabs = () => {
        const { t } = this.props;
        const { tab } = this.state;

        return (
            <MUITabsWrapper
                value={tab}
                onChange={(_e, v) => this.setState({ tab: v })}
            >
                <Tab
                    value="notes"
                    label={t('Common.notes_and_recommendations')}
                />
                <Tab value="csv" label={t('Common.csv_upload')} />
            </MUITabsWrapper>
        );
    };

    render() {
        const { t } = this.props;
        const { tab } = this.state;

        return (
            <>
                <PageTitle title={t('Notes.community')} />
                {this.renderTabs()}
                {tab === 'notes' && (
                    <Paper elevation={0} variant="outlined">
                        {this.renderTable()}
                    </Paper>
                )}
                {tab === 'csv' && this.renderCSVUpload()}
            </>
        );
    }
}

export default withTranslation()(Dashboard);
